/**
 * SAccordion Component - MUI Accordion-based Collapsible Section
 *
 * A collapsible section component built on MUI Accordion with enhanced features:
 * - Field update tracking and visual indicators
 * - Help system integration
 * - Auto-expand on updates
 * - Controlled and uncontrolled modes
 * - Theme-aware styling
 * - Nesting prevention
 *
 * Replaces SmartSection with better accessibility and MUI foundation.
 */

import React, { useState, useContext, useMemo, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { HelpTooltip } from '../HelpTooltip';
import { GuideAnchorHelp } from '../GuideAnchorContext';
import { FieldUpdateContext } from '../../contexts/FieldUpdateContext';
import { FieldUpdateContextValue } from '../../types';
import { SectionUpdateIndicator } from '../FieldUpdateIndicator';

// ============================================================================
// TYPES
// ============================================================================

export interface SAccordionProps {
  /**
   * Section title (required)
   */
  title: string;

  /**
   * Optional subtitle
   */
  subtitle?: string;

  /**
   * Section content
   */
  children: React.ReactNode;

  /**
   * Whether the section can be collapsed
   * @default false
   */
  collapsible?: boolean;

  /**
   * Initial expanded state (uncontrolled mode)
   * @default true
   */
  defaultExpanded?: boolean;

  /**
   * Initial collapsed state (uncontrolled mode) - alternative to defaultExpanded
   * @default false
   */
  defaultCollapsed?: boolean;

  /**
   * Controlled expanded state
   * When provided, component operates in controlled mode
   */
  expanded?: boolean;

  /**
   * Callback when section is toggled
   * @param expanded - New expanded state
   */
  onChange?: (expanded: boolean) => void;

  /**
   * Optional icon to display before title
   */
  icon?: React.ReactNode;

  /**
   * Help content ID for help system integration
   */
  helpId?: string;

  /**
   * Static help content (overrides helpId)
   */
  helpContent?: string;

  /**
   * Callback when help is clicked
   */
  onHelpClick?: (helpId?: string) => void;

  /**
   * Callback when section is toggled (only for collapsible sections)
   * @deprecated Use onChange instead
   */
  onToggle?: (expanded: boolean) => void;

  /**
   * Alternative callback prop name for consistency with other components
   * @deprecated Use onChange instead
   */
  onToggleCollapse?: (collapsed: boolean) => void;

  /**
   * Field path for update tracking (dot notation, e.g., 'screens.0' or 'basicInfo')
   */
  trackUpdatesFor?: string;

  /**
   * Show update indicators for this section
   * @default true when trackUpdatesFor is provided
   */
  showUpdateIndicators?: boolean;

  /**
   * Auto-expand section when updates are detected
   * @default false
   */
  expandOnUpdates?: boolean;

  /**
   * Paper variant (simulated via styling)
   * @default 'outlined'
   */
  variant?: 'elevation' | 'outlined';

  /**
   * Section size
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Whether the section is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Unique ID for this accordion (required when used inside SAccordionGroup)
   */
  id?: string;

  /**
   * Custom styles
   */
  sx?: SxProps<Theme>;

  /**
   * Test ID for testing
   */
  'data-testid'?: string;

  /**
   * CSS class name
   */
  className?: string;

  /**
   * Badge count displayed next to the title (shown when > 0)
   */
  badge?: number;

  /**
   * Badge color variant
   * @default 'warning'
   */
  badgeColor?: 'warning' | 'error' | 'info' | 'success';
}

// ============================================================================
// NESTING PREVENTION
// ============================================================================

// Context to track SAccordion nesting.
// Exported so that composition boundaries (e.g. InlineSectionEditor inside a
// gear-panel Collapse) can reset nesting detection with
// <SAccordionNestingContext.Provider value={false}>.
export const SAccordionNestingContext = React.createContext<boolean>(false);

/** @internal Keep the original name for backward-compatible in-module usage. */
const SAccordionContext = SAccordionNestingContext;

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * SAccordion - MUI Accordion-based collapsible section with enhanced features
 *
 * Usage:
 * ```tsx
 * // Uncontrolled mode
 * <SAccordion title="User Information">
 *   <TextField label="Name" />
 *   <TextField label="Email" />
 * </SAccordion>
 *
 * // Collapsible with default state
 * <SAccordion
 *   title="Advanced Settings"
 *   collapsible
 *   defaultExpanded={false}
 *   icon={<SettingsIcon />}
 * >
 *   <TextField label="API Key" />
 * </SAccordion>
 *
 * // Controlled mode
 * <SAccordion
 *   title="Dynamic Section"
 *   collapsible
 *   expanded={isExpanded}
 *   onChange={(expanded) => setIsExpanded(expanded)}
 * >
 *   <TextField label="Content" />
 * </SAccordion>
 * ```
 */
export const SAccordion: React.FC<SAccordionProps> = ({
  title,
  subtitle,
  children,
  collapsible = false,
  defaultExpanded = true,
  defaultCollapsed,
  expanded: controlledExpanded,
  onChange,
  icon,
  helpId,
  helpContent,
  onHelpClick,
  onToggle,
  onToggleCollapse,
  trackUpdatesFor,
  showUpdateIndicators,
  expandOnUpdates = false,
  variant = 'outlined',
  size = 'medium',
  disabled = false,
  id,
  sx,
  'data-testid': dataTestId,
  className,
  badge,
  badgeColor = 'warning',
}) => {
  const theme = useTheme();
  const fieldUpdateContext = useContext(FieldUpdateContext) as FieldUpdateContextValue | null;
  const isNestedSAccordion = useContext(SAccordionContext);

  // Prevent nesting of SAccordion components
  if (isNestedSAccordion) {
    throw new Error('SAccordion components cannot contain other SAccordion components');
  }

  // Determine if controlled or uncontrolled
  const isControlled = controlledExpanded !== undefined;

  // Prioritize defaultCollapsed if provided, otherwise use defaultExpanded
  const initialExpanded = defaultCollapsed !== undefined ? !defaultCollapsed : defaultExpanded;
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(collapsible ? initialExpanded : true);

  // Use controlled value if provided, otherwise use uncontrolled state
  const isExpanded = isControlled ? controlledExpanded : uncontrolledExpanded;

  // Check for recent updates in this section
  const sectionHasUpdates = useMemo(() => {
    if (!fieldUpdateContext || !trackUpdatesFor) return false;

    const now = Date.now();
    const oneMinuteAgo = now - (60 * 1000);

    return fieldUpdateContext.fieldUpdates.some(update =>
      update.path.startsWith(trackUpdatesFor) &&
      update.timestamp > oneMinuteAgo &&
      update.source !== 'user' // Don't show indicators for user's own changes
    );
  }, [fieldUpdateContext?.fieldUpdates, trackUpdatesFor]);

  // Auto-expand on updates
  useEffect(() => {
    if (sectionHasUpdates && expandOnUpdates && collapsible && !isExpanded) {
      if (isControlled) {
        onChange?.(true);
      } else {
        setUncontrolledExpanded(true);
      }
      onToggle?.(true);
      onToggleCollapse?.(false);
    }
  }, [sectionHasUpdates, expandOnUpdates, collapsible, isExpanded, isControlled, onChange, onToggle, onToggleCollapse]);

  // Determine if we should show update indicators
  const shouldShowIndicators = showUpdateIndicators ?? (trackUpdatesFor !== undefined);
  const hasVisibleUpdates = shouldShowIndicators && sectionHasUpdates;

  const handleChange = (_event: React.SyntheticEvent, newExpanded: boolean) => {
    if (!collapsible || disabled) return;

    // Always call onChange callback if provided
    onChange?.(newExpanded);

    // Update internal state in uncontrolled mode
    if (!isControlled) {
      setUncontrolledExpanded(newExpanded);
    }

    // Legacy callbacks
    onToggle?.(newExpanded);
    onToggleCollapse?.(!newExpanded);
  };

  // Minimal variant styling - only override the divider
  const variantSx: SxProps<Theme> = {
    '&:before': {
      display: 'none', // Remove MUI Accordion default divider
    },
    ...(variant === 'outlined' && {
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: 'none',
    }),
  };

  return (
    <SAccordionContext.Provider value={true}>
      <Accordion
        expanded={!collapsible || isExpanded}
        onChange={handleChange}
        disabled={disabled}
        disableGutters
        className={className}
        data-testid={dataTestId || 'saccordion'}
        sx={{ ...variantSx, ...sx }}
      >
        {/* Header */}
        <AccordionSummary
          expandIcon={collapsible ? <ExpandMoreIcon /> : null}
          aria-controls={id ? `${id}-content` : undefined}
          id={id ? `${id}-header` : undefined}
          sx={{
            ...(hasVisibleUpdates && {
              backgroundColor: alpha(theme.palette.info.main, 0.05),
            }),
            ...(!collapsible && {
              cursor: 'default',
            }),
          }}
        >
          {/* Icon */}
          {icon && <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>}

          {/* Title and Subtitle */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" component="h6" noWrap>
                {title}
              </Typography>
              {badge != null && badge > 0 && (
                <Chip
                  label={badge}
                  size="small"
                  color={badgeColor}
                  sx={{ height: 20, minWidth: 20, fontSize: '0.7rem', fontWeight: 600 }}
                />
              )}
              <GuideAnchorHelp ownerTestId={dataTestId} />
              {hasVisibleUpdates && trackUpdatesFor && (
                <SectionUpdateIndicator
                  sectionPath={trackUpdatesFor}
                  maxIndicators={2}
                  showCount={true}
                />
              )}
            </Box>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>

          {/* Help Icon */}
          {(helpId || helpContent) && (
            <HelpTooltip
              helpId={helpId || ''}
              content={helpContent}
              size="small"
              placement="top"
              onClick={onHelpClick}
              data-testid="saccordion-help"
              component="span"
            />
          )}
        </AccordionSummary>

        {/* Content */}
        <AccordionDetails>
          {children}
        </AccordionDetails>
      </Accordion>
    </SAccordionContext.Provider>
  );
};

SAccordion.displayName = 'SAccordion';

export default SAccordion;
